import React, { useState } from "react";
import { useNavigate, Link, Navigate, useLocation } from "react-router-dom";
import { Loader2, ShieldAlert } from "lucide-react";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const [expired] = useState(() => sessionStorage.getItem("tn_session_expired") === "1");
  const back = location.state?.from || sessionStorage.getItem("tn_return_to") || "";
  const target = back.startsWith("/") && !back.startsWith("/login") ? back : "/dashboard";

  if (user) return <Navigate to={target} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      sessionStorage.removeItem("tn_session_expired");
      sessionStorage.removeItem("tn_return_to");
      navigate(target, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-indigo-600/30 rounded-full blur-3xl" />
        <div className="absolute bottom-0 -left-24 w-96 h-96 bg-sky-500/20 rounded-full blur-3xl" />
        <div className="relative z-10"><Logo onDark /></div>
        <div className="relative z-10 space-y-6">
          <h1 className="font-display text-4xl font-extrabold leading-tight">
            Ship pilots &amp; products with one shared source of truth.
          </h1>
          <p className="text-slate-300 text-base max-w-md">
            Kanban boards, nested subtasks, live notes and a real-time delivery dashboard — built for the Thinkneural team.
          </p>
          <div className="flex gap-6 pt-4 text-sm text-slate-400">
            <div><div className="font-display text-2xl font-bold text-white">Boards</div>delivery &amp; pilots</div>
            <div><div className="font-display text-2xl font-bold text-white">Notes</div>per task &amp; subtask</div>
            <div><div className="font-display text-2xl font-bold text-white">Analytics</div>team workload</div>
          </div>
        </div>
        <div className="relative z-10 text-xs text-slate-500">Internal product delivery workspace</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="lg:hidden mb-2"><Logo /></div>
          <div>
            <h2 className="font-display text-2xl font-bold text-slate-900">Welcome back</h2>
            <p className="text-sm text-slate-500 mt-1">Sign in to your workspace</p>
          </div>

          {expired && (
            <div className="flex items-start gap-2.5 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 animate-in fade-in slide-in-from-top-1 duration-300" role="status" data-testid="session-expired-notice">
              <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
              <span>You were signed out for security, please sign in again.</span>
            </div>
          )}

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="login-error">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@thinkneural.com" required data-testid="login-email-input" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link to="/forgot-password" className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                data-testid="forgot-password-link">Forgot password?</Link>
            </div>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" required data-testid="login-password-input" />
          </div>

          <Button type="submit" disabled={loading} className="w-full bg-indigo-600 hover:bg-indigo-700 h-11"
            data-testid="login-submit-button">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
